interface OnboardingCompletionTimerState {
  isActive: boolean;
  timeRemaining: number | null;
  startTime: number | null;
  timerId: NodeJS.Timeout | null;
  countdownId: NodeJS.Timeout | null;
}

class OnboardingCompletionTimerService {
  private static instance: OnboardingCompletionTimerService;
  private static isCompleting = false;
  private state: OnboardingCompletionTimerState = {
    isActive: false,
    timeRemaining: null,
    startTime: null,
    timerId: null,
    countdownId: null
  };
  private listeners: (() => void)[] = [];
  private hasCompleted = false;
  private lastCompletionTime = 0;

  private constructor() {
    this.restoreFromLocalStorage();
  }

  static getInstance(): OnboardingCompletionTimerService {
    if (!OnboardingCompletionTimerService.instance) {
      OnboardingCompletionTimerService.instance =
        new OnboardingCompletionTimerService();
    }
    return OnboardingCompletionTimerService.instance;
  }

  // Subscribe to timer state changes
  subscribe(listener: () => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  // Notify all listeners
  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener());
  }

  // Get current timer state
  getState(): OnboardingCompletionTimerState {
    return { ...this.state };
  }

  // Start the 5-minute completion timer
  startTimer(
    onComplete: () => void,
    onTimeUpdate?: (timeRemaining: number) => void
  ): void {
    // If timer is already active or has completed, don't start a new one
    if (this.state.isActive || this.hasCompleted) {
      return;
    }

    // Additional check: if there's already a timer ID, don't start another
    if (this.state.timerId) {
      return;
    }

    // Clear any existing timer
    this.clearTimer();

    const delayMs = 5 * 60 * 1000; // 5 minutes
    this.state.isActive = true;
    this.state.timeRemaining = delayMs / 1000;
    this.state.startTime = Date.now();

    // Start countdown timer
    this.state.countdownId = setInterval(() => {
      if (this.state.timeRemaining && this.state.timeRemaining > 1) {
        this.state.timeRemaining -= 1;
        this.notifyListeners();
        onTimeUpdate?.(this.state.timeRemaining);
      } else {
        this.clearTimer();
      }
    }, 1000);

    // Start main timer
    this.state.timerId = setTimeout(async () => {
      // Static check to prevent multiple completions across all instances
      if (OnboardingCompletionTimerService.isCompleting) {
        return;
      }

      // Double-check completion status before proceeding
      if (this.hasCompleted) {
        return;
      }

      const now = Date.now();

      // Prevent multiple completions within 1 second
      if (now - this.lastCompletionTime < 1000) {
        return;
      }

      OnboardingCompletionTimerService.isCompleting = true;
      this.hasCompleted = true;
      this.lastCompletionTime = now;

      try {
        onComplete();
      } catch (error) {
        console.error("❌ Failed to complete onboarding:", error);
      } finally {
        OnboardingCompletionTimerService.isCompleting = false;
        this.clearTimer();
      }
    }, delayMs);

    this.saveToLocalStorage();
    this.notifyListeners();
  }

  // Resume timer if it was active when user returns
  resumeTimer(
    onComplete: () => void,
    onTimeUpdate?: (timeRemaining: number) => void
  ): void {
    if (
      !this.state.isActive ||
      !this.state.timeRemaining ||
      !this.state.startTime ||
      this.state.timeRemaining <= 0 ||
      this.hasCompleted
    ) {
      return;
    }

    // Calculate elapsed time since start
    const elapsedSeconds = Math.floor(
      (Date.now() - this.state.startTime) / 1000
    );
    const remainingTime = Math.max(
      0,
      this.state.timeRemaining - elapsedSeconds
    );

    if (remainingTime <= 0) {
      // Timer should have completed while away - don't complete immediately
      this.clearTimer();
      return;
    }

    // Update remaining time
    this.state.timeRemaining = remainingTime;

    // Timer resumed silently

    // Start countdown timer
    this.state.countdownId = setInterval(() => {
      if (this.state.timeRemaining && this.state.timeRemaining > 1) {
        this.state.timeRemaining -= 1;
        this.notifyListeners();
        onTimeUpdate?.(this.state.timeRemaining);
      } else {
        this.clearTimer();
      }
    }, 1000);

    // Start main timer with remaining time
    this.state.timerId = setTimeout(async () => {
      // Static check to prevent multiple completions across all instances
      if (OnboardingCompletionTimerService.isCompleting) {
        return;
      }

      // Double-check completion status before proceeding
      if (this.hasCompleted) {
        return;
      }

      const now = Date.now();

      // Prevent multiple completions within 1 second
      if (now - this.lastCompletionTime < 1000) {
        return;
      }

      OnboardingCompletionTimerService.isCompleting = true;
      this.hasCompleted = true;
      this.lastCompletionTime = now;

      try {
        onComplete();
      } catch (error) {
        console.error("❌ Failed to complete onboarding:", error);
      } finally {
        OnboardingCompletionTimerService.isCompleting = false;
        this.clearTimer();
      }
    }, remainingTime * 1000);

    this.saveToLocalStorage();
    this.notifyListeners();
  }

  // Clear the timer
  clearTimer(): void {
    if (this.state.timerId) {
      clearTimeout(this.state.timerId);
      this.state.timerId = null;
    }
    if (this.state.countdownId) {
      clearInterval(this.state.countdownId);
      this.state.countdownId = null;
    }
    this.state.isActive = false;
    this.state.timeRemaining = null;
    this.state.startTime = null;
    this.hasCompleted = false; // Reset completion flag
    this.lastCompletionTime = 0;
    OnboardingCompletionTimerService.isCompleting = false; // Reset static flag
    this.saveToLocalStorage();
    this.notifyListeners();
  }

  // Save timer state to localStorage
  private saveToLocalStorage(): void {
    try {
      localStorage.setItem(
        "onboarding-completion-timer-state",
        JSON.stringify({
          isActive: this.state.isActive,
          timeRemaining: this.state.timeRemaining,
          startTime: this.state.startTime
        })
      );
    } catch (error) {
      console.warn(
        "Failed to save completion timer state to localStorage:",
        error
      );
    }
  }

  // Restore timer state from localStorage
  private restoreFromLocalStorage(): void {
    try {
      const saved = localStorage.getItem("onboarding-completion-timer-state");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.isActive && parsed.timeRemaining && parsed.startTime) {
          // Calculate elapsed time since start
          const elapsedSeconds = Math.floor(
            (Date.now() - parsed.startTime) / 1000
          );
          const remainingTime = Math.max(
            0,
            parsed.timeRemaining - elapsedSeconds
          );

          if (remainingTime > 0) {
            // Only restore if there's still time remaining
            this.state.isActive = parsed.isActive;
            this.state.timeRemaining = remainingTime;
            this.state.startTime = parsed.startTime;
            // Timer restored silently
          } else {
            // Timer should have completed while away - don't restore
            this.state.isActive = false;
            this.state.timeRemaining = null;
            this.state.startTime = null;
            // Clear the saved state
            localStorage.removeItem("onboarding-completion-timer-state");
          }
        }
      }
    } catch (error) {
      console.warn(
        "Failed to restore completion timer state from localStorage:",
        error
      );
    }
  }
}

export default OnboardingCompletionTimerService;
